import { Star, Plus } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Carousel } from "../../../../../components/ui/carousel";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../../../../../components/ui/resizable";
import { ReperformanceStepsPanel } from "./ReperformanceStepsPanel";
import { DocumentViewerPanel } from "./DocumentViewerPanel";
import {
  Citation,
  extractTimestampFromCitationString,
} from "../../../../../models/audit/citation";
import { type ViewerBoundingBox } from "../../../../../pages/extraction/viewExtraction/FileViewer";
import { LinkingData } from "../../../../../pages/extraction/viewExtraction/PdfViewer";
import { type BoundingBoxState } from "./types";
import { type AuditSession } from "../../../../../models/audit/auditSession";

/**
 * Formats timestamp in seconds to M:SS format for video citations
 */
function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface WalkthroughTabContentProps {
  reperformanceSteps: AuditSession["sourceRows"][number]["attributeTestResults"][number]["reperformanceSteps"];
  filteredCitations: Citation[];
  activeEvidenceIndex: number;
  focusedStepId: string | undefined;
  currentBoundingBox: BoundingBoxState | undefined;
  viewerBoundingBoxes: ViewerBoundingBox[] | undefined;
  selectedBoundingBoxId: string | undefined;
  selectedBoundingBoxIndex: number | undefined;
  currentSelectionValue: string | undefined;
  showDocumentViewer: boolean;
  allCitations: Citation[];
  onStepToggle: (stepId: string | undefined) => void;
  onCitationClick: (citation: Citation) => void;
  onAutoSelectCitation?: (citation: Citation) => void;
  onEvidenceIndexChange: (index: number) => void;
  onLinkingComplete: (linkingData: LinkingData) => Promise<void>;
  onDelete: (params: { id?: string; index: number }) => Promise<void>;
  onSelect: (params: { id?: string }) => void;
  onToggleKeyCitation: () => Promise<void>;
  onAddCitation?: () => void;
  onEditCitationSummary?: (citation: Citation) => void;
}

export function WalkthroughTabContent({
  reperformanceSteps,
  filteredCitations,
  activeEvidenceIndex,
  focusedStepId,
  currentBoundingBox,
  viewerBoundingBoxes,
  selectedBoundingBoxId,
  selectedBoundingBoxIndex,
  currentSelectionValue,
  showDocumentViewer,
  allCitations,
  onStepToggle,
  onCitationClick,
  onAutoSelectCitation,
  onEvidenceIndexChange,
  onLinkingComplete,
  onDelete,
  onSelect,
  onToggleKeyCitation,
  onAddCitation,
  onEditCitationSummary,
}: WalkthroughTabContentProps) {
  return (
    <ResizablePanelGroup direction="horizontal" className="h-full">
      <ResizablePanel defaultSize={35} minSize={25} maxSize={60}>
        <div className="h-full flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto px-4">
            <div className="space-y-4">
              <ReperformanceStepsPanel
                reperformanceSteps={reperformanceSteps}
                focusedStepId={focusedStepId}
                currentBoundingBox={currentBoundingBox}
                onStepToggle={onStepToggle}
                onCitationClick={onCitationClick}
                onAutoSelectCitation={onAutoSelectCitation}
                onEditCitationSummary={onEditCitationSummary}
              />
            </div>
          </div>

          <div className="border-t p-2 border-r bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-font-secondary font-mono">
                Key Evidence
              </div>
              {onAddCitation && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={onAddCitation}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Citation
                </Button>
              )}
            </div>
            {filteredCitations.length > 0 && (
              <Carousel
                items={filteredCitations.map((citation, i) => {
                  const timestamp = extractTimestampFromCitationString(
                    citation.encodedCitationString,
                  );
                  const isVideoCitation = timestamp !== undefined;
                  const label = isVideoCitation
                    ? `@ ${formatTimestamp(timestamp)}`
                    : i + 1;

                  return {
                    id: i,
                    content:
                      citation.isKey === true ? (
                        <span className="flex items-center gap-1">
                          <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
                          {label}
                        </span>
                      ) : (
                        label
                      ),
                  };
                })}
                selectedIndex={activeEvidenceIndex}
                onItemSelect={(i) => {
                  const next = Math.max(
                    0,
                    Math.min(i, filteredCitations.length - 1),
                  );
                  onEvidenceIndexChange(next);
                }}
              />
            )}
          </div>
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      <ResizablePanel defaultSize={65} minSize={40}>
        <div className="h-full overflow-x-auto min-h-0 flex flex-col">
          <DocumentViewerPanel
            showDocumentViewer={showDocumentViewer}
            currentBoundingBox={currentBoundingBox}
            viewerBoundingBoxes={viewerBoundingBoxes}
            selectedBoundingBoxId={selectedBoundingBoxId}
            selectedBoundingBoxIndex={selectedBoundingBoxIndex}
            currentSelectionValue={currentSelectionValue}
            allCitations={allCitations}
            onLinkingComplete={onLinkingComplete}
            onDelete={onDelete}
            onSelect={onSelect}
            onToggleKeyCitation={onToggleKeyCitation}
            onAddCitation={onAddCitation}
          />
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
